import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SignatureType, DocumentWorkflowTemplateType } from '@dealstack/shared/lib/generated';
import { createDocumentWorkflowTemplate } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { useFileUpload } from '@dealstack/frontend/shared/components/Documents/hooks/useFileUpload';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useClassifierOptions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useClassifierOptions.ts';
import { useMasterDocumentsEndpoints } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useMasterDocumentsEndpoints.ts';
import { fileNameToDocumentName } from '@dealstack/shared/lib/common/file/file';
import {
	MasterDocumentFormDto,
	MASTER_DOCUMENT_FORM_FIELD,
	MasterDocumentCreateShape,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentForm/types.ts';
import { getSignatureType } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/getSignatureType.ts';
import { supportsWitnessField } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/supportsWitnessField';

export const useCreateMasterDocumentDrawer = (refreshList: () => void, onClose: () => void) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();
	const { list: listKey, create: createEndpoint } = useMasterDocumentsEndpoints();

	const { uploadedFile, setUploadedFile, removeFile, handleFileUpload, isUploading } =
		useFileUpload();

	const createRequest = useMutation({
		mutationFn: async (body: Parameters<typeof createDocumentWorkflowTemplate>[0]['body']) => {
			return await createDocumentWorkflowTemplate({ endpoint: createEndpoint, body });
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: [listKey] });
			refreshList();
			onClose();
			showToastV2({
				title: tMipManage('master_documents.created'),
			});
		},
	});

	const classifierOptions = useClassifierOptions();

	const defaultValues: MasterDocumentFormDto = useMemo(
		() => ({
			[MASTER_DOCUMENT_FORM_FIELD.name]: '',
			[MASTER_DOCUMENT_FORM_FIELD.type]: DocumentWorkflowTemplateType.SIGNATURE_REQUIRED,
			[MASTER_DOCUMENT_FORM_FIELD.classifiers]: [],
			[MASTER_DOCUMENT_FORM_FIELD.visibleIn]: [],
			[MASTER_DOCUMENT_FORM_FIELD.signatureType]: SignatureType.ELECTRONIC,
			[MASTER_DOCUMENT_FORM_FIELD.counterparties]: [],
			[MASTER_DOCUMENT_FORM_FIELD.witnessRequired]: false,
			[MASTER_DOCUMENT_FORM_FIELD.individualScheduleRequired]: false,
			[MASTER_DOCUMENT_FORM_FIELD.individualInstances]: true,
			[MASTER_DOCUMENT_FORM_FIELD.instruction]: '',
			[MASTER_DOCUMENT_FORM_FIELD.useManualSignaturePlacement]: false,
			[MASTER_DOCUMENT_FORM_FIELD.showStampMetadata]: true,
			[MASTER_DOCUMENT_FORM_FIELD.requiresPostDating]: false,
			[MASTER_DOCUMENT_FORM_FIELD.signaturePlacements]: [],
			[MASTER_DOCUMENT_FORM_FIELD.documentSource]: 'upload',
		}),
		[]
	);

	const form = useForm<MasterDocumentFormDto>({
		mode: 'all',

		resolver: zodResolver(MasterDocumentCreateShape),
		defaultValues,
		shouldFocusError: false,
	});

	const lastPrepopulatedNameRef = useRef<string | null>(null);

	const setUploadedFileAndPrepopulateName = (file: File | null) => {
		setUploadedFile(file);
		if (!file) {
			return;
		}
		const currentName = form.getValues(MASTER_DOCUMENT_FORM_FIELD.name)?.trim() ?? '';
		const wasAutoPopulated = currentName === lastPrepopulatedNameRef.current;
		if (currentName && !wasAutoPopulated) {
			return;
		}
		const newName = fileNameToDocumentName(file.name);
		form.setValue(MASTER_DOCUMENT_FORM_FIELD.name, newName, {
			shouldDirty: true,
			shouldValidate: true,
		});
		lastPrepopulatedNameRef.current = newName;
	};

	const submitMasterDocument = async (formValues: MasterDocumentFormDto) => {
		const isTemplateSource = formValues.documentSource === 'template';

		let sourceFields: { file: { id: string } } | { globalDocumentTemplateId: string };
		if (isTemplateSource) {
			if (!formValues.globalDocumentTemplateId) {
				showToastV2({
					title: tMipManage('master_documents.no_template'),
					state: 'error',
				});
				return;
			}
			sourceFields = { globalDocumentTemplateId: formValues.globalDocumentTemplateId };
		} else {
			if (!uploadedFile) {
				showToastV2({
					title: tMipManage('master_documents.no_file'),
					state: 'error',
				});
				return;
			}

			const fileId = await handleFileUpload();
			if (!fileId) {
				return;
			}
			sourceFields = { file: { id: fileId } };
		}

		const isSignatureRequired =
			formValues.type === DocumentWorkflowTemplateType.SIGNATURE_REQUIRED;

		const counterparties = isSignatureRequired ? formValues.counterparties ?? [] : [];
		const signatureType = getSignatureType({
			type: formValues.type,
			formSignatureType: formValues.signatureType,
		});

		const submittedIndividualInstances = isSignatureRequired
			? formValues.individualInstances
			: true;
		const requiredWitnesses =
			isSignatureRequired &&
			Boolean(formValues.witnessRequired) &&
			supportsWitnessField({
				signatureType,
				individualInstances: submittedIndividualInstances,
			});
		const body = {
			name: formValues.name,
			type: formValues.type,
			signatureType,
			...sourceFields,
			counterparties,
			instruction: formValues.instruction,
			requiredWitnesses,
			visibleIn: formValues.visibleIn,
			classifiers: formValues.classifiers,
			individualScheduleRequired: submittedIndividualInstances
				? formValues.individualScheduleRequired
				: false,
			individualInstances: submittedIndividualInstances,
			triggerInvestorReevaluate: true,
		};

		await createRequest.mutateAsync(body);
		form.reset(defaultValues);
		removeFile();
		lastPrepopulatedNameRef.current = null;
	};

	const isSubmittingRef = useRef(false);

	const onSubmit = () => async (formValues: MasterDocumentFormDto) => {
		// Belt-and-braces against a double click landing before React re-renders the
		// disabled button — one click must never create two master documents.
		if (isSubmittingRef.current) {
			return;
		}
		isSubmittingRef.current = true;
		try {
			await submitMasterDocument(formValues);
		} catch {
			// The global mutation handler already surfaces the error toast.
		} finally {
			isSubmittingRef.current = false;
		}
	};

	return {
		createRequest,
		// Covers the whole submit — file upload included — not just the create request.
		isSubmitting: form.formState.isSubmitting || isUploading || createRequest.isPending,
		onSubmit,
		form,
		classifierOptions,
		uploadedFile,
		setUploadedFile: setUploadedFileAndPrepopulateName,
		removeFile,
	};
};
